import { useToast } from '@/app/providers';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { Guest } from '@/shared/types';
import { Calendar, Car, Clock, Pencil, QrCode, Share2, Trash2, X } from 'lucide-react';
import QRCode from 'qrcode';
import { padQRCodeValue } from '@yawi/domain';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../atoms/Button';

interface GuestListProps {
  guests: Guest[];
  onDelete: (id: string, name: string) => void;
  onEdit?: (guest: Guest) => void;
  refreshGuest?: (guestId: string) => Promise<Guest | null>;
}

export const GuestList: React.FC<GuestListProps> = ({ guests, onDelete, onEdit, refreshGuest }) => {
  const { t } = useI18n();
  const toast = useToast();
  const { formatDateTime } = useDateFormatter();
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [qrUrl, setQrUrl] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = () => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    stopPoll();

    if (!selectedGuest) {
      setQrUrl('');
      return;
    }

    // Always generate local QR with zero-padded value (padding is for physical readers)
    QRCode.toDataURL(padQRCodeValue(selectedGuest.numericCode), { width: 200, margin: 2 })
      .then(setQrUrl)
      .catch(console.error);

    if (refreshGuest) {
      const guestId = selectedGuest.id;
      pollRef.current = setInterval(async () => {
        const fresh = await refreshGuest(guestId);
        if (fresh?.qrCodeImage) {
          stopPoll();
          setSelectedGuest(fresh);
        }
      }, 2000);
    }

    return stopPoll;
  }, [selectedGuest, refreshGuest]);

  const getStatus = (validTo: string) => {
    const now = new Date();
    const end = new Date(validTo);
    // If validTo is more than 50 years, it's permanent
    const isPermanent = end.getFullYear() - now.getFullYear() > 50;
    
    if (end < now) return { label: t('guest.status.expired'), color: 'bg-slate-100 text-slate-500 border-slate-200' };
    if (isPermanent) return { label: 'PERMANENTE', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    return { label: t('guest.status.active'), color: 'bg-blue-50 text-blue-700 border-blue-200' };
  };

  const handleShare = (guest: Guest) => {
    const text = `Acceso Hommi\nInvitado: ${guest.name}\nPIN: ${guest.numericCode}\nVálido hasta: ${formatDateTime(guest.validTo)}`;
    if (navigator.share) {
      navigator.share({
        title: 'Acceso Residencial',
        text: text,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    }
  };

  if (guests.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-dashed border-slate-300">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
          <QrCode size={32} />
        </div>
        <p className="text-slate-500">{t('guest.empty')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4">
        {guests.map((guest) => {
          const status = getStatus(guest.validTo);
          const isExpired = new Date(guest.validTo) < new Date();
          return (
            <div key={guest.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 transition-all hover:shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0 font-bold text-lg">
                  {guest.name.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-bold text-slate-800">{guest.name}</h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-bold ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Car size={12}/> {guest.plateNumber || 'N/A'}</span>
                    <span className="flex items-center gap-1"><Clock size={12}/> {t(`guest.type.${guest.type.toLowerCase()}` as any)}</span>
                    <span className="flex items-center gap-1"><Calendar size={12}/> {formatDateTime(guest.validTo)}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {guest.allowedDeviceIds && guest.allowedDeviceIds.length > 0
                      ? guest.allowedDeviceIds.map(id => (
                          <span key={id} className="text-[10px] px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-600 rounded font-medium">{id.slice(0, 8)}</span>
                        ))
                      : <span className="text-[10px] px-1.5 py-0.5 bg-slate-50 border border-slate-200 text-slate-400 rounded">Todas las puertas</span>
                    }
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 border-t md:border-t-0 pt-3 md:pt-0 border-slate-100 justify-end">
                {onEdit && (
                  <button
                    onClick={() => onEdit(guest)}
                    className="p-2 text-slate-400 hover:text-primary-600 transition-colors rounded-lg hover:bg-primary-50"
                    title="Editar invitación"
                  >
                    <Pencil size={18} />
                  </button>
                )}
                 <button
                  onClick={() => onDelete(guest.id, guest.name)}
                  className="p-2 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title={t('common.delete')}
                >
                  <Trash2 size={18} />
                </button>
                {!isExpired && (
                  <Button variant="secondary" size="sm" icon={<Share2 size={14} />} onClick={() => handleShare(guest)}>
                    PIN
                  </Button>
                )}
                <Button variant="primary" size="sm" icon={<QrCode size={14} />} onClick={() => setSelectedGuest(guest)}>
                   QR
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ticket Modal Overlay */}
      {selectedGuest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Ticket Header */}
            <div className="bg-slate-900 text-white p-5 text-center relative">
              <button 
                onClick={() => setSelectedGuest(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
              <h3 className="text-lg font-bold tracking-wider">{t('guest.ticket.title')}</h3>
              <p className="text-slate-400 text-xs uppercase tracking-widest">{selectedGuest.type}</p>
            </div>

            {/* Ticket Body */}
            <div className="p-6 flex flex-col items-center text-center relative">
              {/* Punch hole effect */}
              <div className="absolute top-0 left-0 w-full h-4 -mt-2">
                 <div className="w-4 h-4 rounded-full bg-slate-900/60 absolute -left-2 top-0"></div>
                 <div className="w-4 h-4 rounded-full bg-slate-900/60 absolute -right-2 top-0"></div>
                 <div className="border-t-2 border-dashed border-slate-200 w-full mt-2"></div>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mb-1">{selectedGuest.name}</h2>
              <p className="text-sm text-slate-500 mb-6">
                {selectedGuest.plateNumber ? `Placa: ${selectedGuest.plateNumber}` : 'Acceso Peatonal'}
              </p>

              <div className="bg-white p-2 rounded-xl border-4 border-slate-900 mb-6 shadow-inner">
                {qrUrl ? (
                  <img 
                    src={qrUrl} 
                    alt="QR Access" 
                    className="w-48 h-48 object-contain"
                  />
                ) : (
                   <div className="w-48 h-48 flex items-center justify-center bg-slate-50 rounded-lg">
                     <QrCode className="text-slate-300 animate-pulse" size={48} />
                   </div>
                )}
              </div>

              <div className="w-full bg-slate-50 rounded-lg p-3 border border-slate-200 mb-6">
                 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('guest.ticket.pin')}</p>
                 <div className="text-3xl font-mono font-bold text-slate-800 tracking-[0.2em]">
                   {selectedGuest.numericCode}
                 </div>
              </div>

              <div className="text-xs text-slate-400 mb-4">
                <p>{t('guest.ticket.valid_until')}</p>
                <p className="font-medium text-slate-600">{formatDateTime(selectedGuest.validTo)}</p>
              </div>

              {new Date(selectedGuest.validTo) >= new Date() && (
                <Button className="w-full" onClick={() => handleShare(selectedGuest)} icon={<Share2 size={16}/>}>
                  {t('guest.ticket.share_msg')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};